
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import {defineComponent, PropType} from "vue";
import {TradeContractFormatGroup} from "@/store/modules/hcs/tradeContractFormatGroup/model";
import FormatGroupTable from "@/components/pages/contract/TradeContractFormatGroup/FormatGroupTable.vue";
import IconBtn from "@/components/molecules/IconBtn.vue";
import ioablePageMixin from "@/router/IoablePageMixin";
import {AxiosInstance} from "axios";
import TableInline from "@/components/atoms/TableInline.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import {mapActions, mapMutations} from "vuex";
import Swal from "sweetalert2";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";

export default defineComponent({
  name: "SelectedFormatGroupTab",
  components: {
    ReadonlyCheckbox,
    SimpleDialog,
    TableInline,
    IconBtn,
    FormatGroupTable,
    TabItemTemplate,
  },
  mixins: [
    ioablePageMixin,
  ],
  emits: [
    'change:groups',
  ],
  props: {
    groups: {
      type: Array as PropType<TradeContractFormatGroup[]>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    hideToolbar: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  inject: ['tabItemBounds'],
  data() {
    return {
      groupsAll: [] as TradeContractFormatGroup[],
      groupSelectedIds: [] as string[],
      createDialog: {
        visible: false,
        form: {
          name: '',
          description: '',
        } as TradeContractFormatGroup,
      }
    };
  },
  computed: {
    hcsClient(): AxiosInstance {
      const mayAxios = this.$store?.getters?.['hcs/client'];
      if(mayAxios) {
        return mayAxios;
      }
      throw new Error("store is null");
    },
  },
  methods: {
    ...mapActions({
      fetchTradeContractFormatGroups: "hcs/tradeContractFormatGroup/findList",
      deleteTradeContractFormatGroups: "hcs/tradeContractFormatGroup/delete",
      createTradeContractFormatGroups: "hcs/tradeContractFormatGroup/create",
    }),
    ...mapMutations({
      showSnackbar: 'showSnackbar',
    }),
    async load() {
      const {data: groups} = await this.hcsClient.get<TradeContractFormatGroup[]>(`/trade_contract_format_groups`)
      this.groupsAll = groups;
    },
    emitChangeGroups() {
      const selection = this.groupsAll.filter(g => this.groupSelectedIds.includes(g.id));
      this.$emit('change:groups', selection);
    },
    handleSelectedChange(row: TradeContractFormatGroup, isSelected: boolean) {
      const index = this.groupSelectedIds.findIndex(s => row.id === s);
      if(isSelected && index === -1) {
        this.groupSelectedIds.push(row.id);
        this.emitChangeGroups();
      } else if(!isSelected && index !== -1) {
        this.groupSelectedIds.splice(index, 1);
        this.emitChangeGroups();
      }
    },
    async handleDeleteButtonClick(row: TradeContractFormatGroup) {
      const {value: confirm} = await Swal.fire({
        title: '確認',
        icon: 'info',
        html: `本当に取引契約フォーマットグループ <strong>${row.name}</strong> を削除してよろしいですか。`,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ"
      });
      if(!confirm) {
        return;
      }

      const loading = this.$loading({
        lock: true,
        spinner: 'el-icon-loading',
      });
      try {
        await this.deleteTradeContractFormatGroups(row.id);
        const i = this.groupsAll.findIndex(g => g.id === row.id);
        if(i !== -1) {
          this.groupsAll.splice(i, 1);
        }
        const j = this.groupSelectedIds.indexOf(row.id);
        if(j !== -1) {
          this.groupSelectedIds.splice(j, 1);
          this.emitChangeGroups();
        }

        this.showSnackbar({
          message: `取引契約フォーマットグループ ${row.name} を削除しました`,
        });
      } catch (e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `取引契約フォーマットグループ ${row.name} の削除に失敗しました`,
        });
      } finally {
        loading.close();
      }
    },
    handleCreateButtonClick() {
      this.createDialog = {
        visible: true,
        form: {
          name: '',
          description: '',
        } as TradeContractFormatGroup,
      }
    },
    async handleCreateDialogClose(apply: boolean) {
      if(apply) {
        const loading = this.$loading({
          lock: true,
          spinner: 'el-icon-loading',
        });
        try {
          const form = this.createDialog.form;
          const created = await this.createTradeContractFormatGroups(form);

          this.groupsAll.push(created);
          this.groupSelectedIds.push(created.id);
          this.emitChangeGroups();
          this.showSnackbar({
            message: `取引契約フォーマットグループ ${form.name} を作成しました`,
          });
        } catch (e) {
          console.error(e);
          this.showSnackbar({
            color: 'failed',
            message: `取引契約フォーマットグループの作成に失敗しました`,
          });
        } finally {
          loading.close();
        }
      }
      this.createDialog.visible = false;
    },
  },
  watch: {
    groups: {
      immediate: true,
      handler(groups: TradeContractFormatGroup[]) {
        this.groupSelectedIds = groups.map(g => g.id);
      }
    }
  }
});
